// Central content + imagery for the Nur Therapy landing page.

export const HERO_IMAGE =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_nurtherapy-white/artifacts/390xws4t_shutterstock_1448071919-2.jpg";

export const ABOUT_IMAGE =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_nurtherapy-white/artifacts/xzpscnma_SCMR_Resilience_2_Getty_600px_1218.jpg";

export const LEAF_IMAGE =
  "https://images.unsplash.com/photo-1533038590840-1cde6e668a91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxncmVlbiUyMGxlYXZlcyUyMGJvdGFuaWNhbCUyMHRleHR1cmUlMjBtaW5pbWFsfGVufDB8fHx8MTc4NTE4MDg0OHww&ixlib=rb-4.1.0&q=85";

export const NAV_LINKS = [
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery" },
  { label: "Treatments", id: "pricing" },
  { label: "Contact", id: "contact" },
];

export const CHAPTERS = [
  {
    no: "01",
    title: "The Meaning of Nur",
    body:
      "Nur means light. It is the quiet glow that returns when the body softens and the mind grows still. Every session begins here — with the simple intention of helping you find the balance to break through life challenges and heal, so you can grow.",
  },
  {
    no: "02",
    title: "The Practice",
    body:
      "Holistic by nature and intuitive by design, we bring our full attention and pure intent to help readdress your balance.",
  },
  {
    no: "03",
    title: "Rooted in Nature",
    body:
      "Nestled in Woodford Green, on the edge of Epping Forest, where quiet and peace meet light pouring through the trees. Our space is an extension of nature itself — grounded, unrushed, relaxed, and deeply restorative.",
  },
];

export const SERVICES = [
  {
    id: "tcm",
    name: "TCM & Acupuncture",
    copy: "Rooted in thousands of years of Traditional Chinese Medicine, fine and virtually painless needles are placed along the body's meridians to restore the free flow of qi — easing pain, calming the mind and rebalancing body and spirit.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/ec9164cc-b7e8-415d-a19b-54ca525b86c5/images/2fe7fe739516bebf26bf25e24c770b7f4f9c4682380f9ed62d82c4d65ed610bb.jpeg",
  },
  {
    id: "hijama",
    name: "Hijama Cupping Therapy",
    copy: "The ancient Sunnah practice of wet cupping works on many levels. Physically, it boosts the circulation of blood and oxygen while drawing out stagnation and toxins — relieving pain, supporting the body's natural healing and leaving you lighter and renewed.",
    image:
      "https://images.pexels.com/photos/8312830/pexels-photo-8312830.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "massage",
    name: "Ayurvedic Massage",
    copy: "A blend of Malaysian and Ayurvedic massage techniques, using carefully judged levels of pressure to target and relieve musculoskeletal issues. Warm herbal oils and considered, therapeutic movements ease tight muscles, improve mobility and restore balance to both body and mind.",
    image:
      "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
  {
    id: "sauna",
    name: "Sauna Therapy",
    copy: "Gentle near-infrared heat warms the body from within, so you sweat deeply at a comfortable temperature — easing out toxins through the skin, boosting circulation and oxygenation, stimulating the internal organs and calming the nervous system.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/ec9164cc-b7e8-415d-a19b-54ca525b86c5/images/f05da54b277fb35c28ec95ba620183ab45c9a9cb7174b95e26fa213a7ca03172.jpeg",
  },
  {
    id: "phyto",
    name: "Phytobiophysics & PMT",
    copy: "Rebalancing body and mind with the energy of plants and flowers. Vibrational flower formulae work on the frequency of colour to restore harmony, while Podo-Rhacidian (PMT) — a gentle structural therapy — realigns the body's musculoskeletal and energetic systems.",
    contain: true,
    image: "/phyto-disc.png",
  },
];

export const PAIN_CATEGORIES = [
  {
    name: "Acute",
    desc: "Symptoms start suddenly, usually last a short time and can be mild or severe.",
  },
  {
    name: "Subacute",
    desc: "Symptoms last longer than an acute problem but have not yet become chronic.",
  },
  {
    name: "Chronic",
    desc: "Symptoms develop slowly or persist for a long time — often lasting months to years.",
  },
];

export const TREATMENT_GROUPS = [
  {
    name: "TCM & Acupuncture",
    summary: "Traditional Chinese acupuncture to restore balance.",
    items: [
      {
        name: "TCM Acupuncture",
        desc: "A traditional Chinese acupuncture session with fine, painless needles to restore balance and the free flow of qi.",
        price: "£75",
        followUp: "£55",
      },
    ],
  },
  {
    name: "Hijama Cupping Therapy",
    summary: "Targeted massage paired with cupping to release tension and pain.",
    note: "Additional cups are chargeable at £2.50 per cup.",
    items: [
      {
        name: "Standard Package",
        duration: "10 min",
        desc: "A 10-minute targeted massage followed by cupping with 10 cups to release everyday tension.",
        price: "£45",
      },
      {
        name: "Standard Pain Relief Package",
        duration: "25 min",
        desc: "A 25-minute massage plus cupping with 10 cups, focused on easing a specific area of pain.",
        price: "£65",
      },
      {
        name: "Elite Pain Relief Package",
        duration: "60 min",
        desc: "A full 60-minute massage plus cupping with 10 cups for deeper, widespread or chronic pain.",
        price: "£90",
      },
    ],
  },
  {
    name: "Phytobiophysics & PodoRhacidian",
    summary: "Vibrational flower formulae and gentle structural therapy to restore balance.",
    items: [
      {
        name: "Initial & Heart Lock Session",
        desc: "An initial session, followed by a Heart Lock session where there is any deep, unacknowledged trauma to release.",
        price: "£75",
        followUp: "£45 (plus remedies)",
      },
    ],
  },
  {
    name: "Ayurvedic Massage",
    summary: "Full-body massage to unwind and release tension.",
    items: [
      {
        name: "Massage",
        duration: "60 or 120 min",
        desc: "A full-body massage to unwind and release tension. Available as 60 minutes, or a 2-hour session (men only).",
        price: "£65 / £130",
      },
    ],
  },
  {
    name: "Sauna Therapy",
    summary: "Gentle infrared heat that helps the body detoxify, improves circulation and oxygenation, stimulates the internal organs and deeply calms the nervous system.",
    note: "Includes aftercare guidance on hydration and rest. Please tell us about any medical conditions or if you are pregnant when booking.",
    items: [
      {
        name: "Sauna Session",
        duration: "15 min",
        desc: "The gentlest first step into infrared heat — easing the body in, warming gently and calming the nervous system.",
        price: "£25",
      },
      {
        name: "Sauna Session",
        duration: "20 min",
        desc: "A soothing introductory session — infrared warmth to relax the nervous system, open the skin and begin releasing toxins through sweat.",
        price: "£35",
      },
      {
        name: "Sauna Session",
        duration: "30 min",
        desc: "A deeper sweat to boost circulation and oxygenation, decongest the internal organs and support the immune system.",
        price: "£45",
      },
      {
        name: "Sauna Session",
        duration: "45 min",
        desc: "A longer session for deeper detoxification, cardiovascular support and full-body renewal.",
        price: "£55",
      },
      {
        name: "Sauna Session",
        duration: "60 min",
        desc: "Our fullest single session, for when you've built up to it — maximum detoxification and deep restoration.",
        price: "£65",
      },
      {
        name: "Tailored Sauna Programme",
        desc: "A personalised course of regular sessions built around your goal — recovery from illness, stimulating sluggish organs, or supporting weight management and metabolism. Designed for consistent use over several weeks.",
        price: "On consultation",
      },
    ],
  },
  {
    name: "Nur Signature Package",
    summary: "Our complete, bespoke protocol weaving every modality together.",
    featured: true,
    items: [
      {
        name: "Nur Signature Package",
        duration: "135 min",
        desc: "Begins with a 15-minute infrared sauna to warm and prepare the body, followed by a combination of protocols catered to you — Massage, Acupuncture, Cupping, Phytobiophysics and PMT.",
        price: "£175",
      },
    ],
  },
];

export const MARQUEE_WORDS = [
  "Eat Well",
  "Holistic Health",
  "Acupuncture",
  "Ayurvedic Massage",
  "Phytobiophysics",
  "Podo Rhacidian",
  "Hijama Cupping",
];

export const ENQUIRY_OPTIONS = [
  "TCM & Acupuncture",
  "Hijama Cupping Therapy",
  "Ayurvedic Massage",
  "Phytobiophysics & PMT",
  "Standard Package (Massage & Cupping)",
  "Standard Pain Relief Package",
  "Elite Pain Relief Package",
  "Massage (60 or 120 min)",
  "Sauna Therapy",
  "Tailored Sauna Programme",
  "Nur Signature Package",
  "General enquiry / Not sure yet",
];

export const FAQS = [
  {
    q: "How do I book an appointment?",
    a: "We see clients Tuesday to Sunday, by appointment only. The easiest way to book is to call or text us on +44 7984 341 251, and we'll find a time that suits you.",
  },
  {
    q: "Can I choose a male or female therapist?",
    a: "Yes. For comfort and modesty, Nuriyah treats female clients only and Farid treats male clients only. Let us know who you'd like to see when you book.",
  },
  {
    q: "What should I wear and what happens during a treatment?",
    a: "Wear something comfortable and loose. During your treatment your body stays fully covered — only the area being worked on at that moment is ever exposed.",
  },
  {
    q: "What is your cancellation policy?",
    a: "Please give us at least 24 hours' notice if you need to cancel or reschedule. Late cancellations or missed appointments may be charged in full, as the time is reserved just for you.",
  },
  {
    q: "What if I'm running late?",
    a: "Please try to arrive about 10 minutes before your appointment. If you're running late we'll do our best, but your treatment may be shortened so the following clients aren't kept waiting.",
  },
  {
    q: "Should I mention medical conditions or pregnancy?",
    a: "Absolutely — please tell us about any allergies, injuries, medical conditions or if you are pregnant when you book. This lets us adapt your treatment and keep you safe.",
  },
  {
    q: "Is Hijama cupping safe and hygienic?",
    a: "Yes. Hygiene is our highest priority. We follow strict cleanliness standards and use sterile, single-use equipment for every wet cupping session.",
  },
  {
    q: "How many sessions will I need?",
    a: "It depends on you and what you're carrying. We look at the root cause rather than just the symptoms, and will suggest a personalised plan — often a course of sessions with follow-ups — after your first visit.",
  },
];

export const TEAM_IMAGE = "/team-photo-v2.jpg";

export const TEAM = [
  {
    id: "nuriyah",
    name: "Nuriyah Bansi",
    creds: "Dip.TCMAcu, MAcS, Dip.HCT",
    role: "Co-Founder & Holistic Therapist",
    sees: "Female clients only",
    bio: "Nuriyah is a dedicated acupuncturist with over 15 years' experience helping women restore balance and reconnect with the body's natural ability to heal. Her own healing journey drew her to acupuncture, plant medicine and traditional therapies practised across cultures for centuries. She approaches her patients with care and attention, looking at the root causal factors rather than simply easing symptoms. In a calm, welcoming space, she guides every woman towards greater health, harmony and vitality.",
  },
  {
    id: "farid",
    name: "Farid Ameen",
    creds: "BSc, Dip.AMPT, Dip.HCT",
    role: "Co-Founder & Holistic Therapist",
    sees: "Male clients only",
    bio: "Farid is a versatile, dedicated therapist with a genuine passion for service. He supports healing in many ways, blending Ayurvedic and Malaysian massage into his own signature technique, alongside dry needling, cupping and PodoRhacidian Mobilisation. Every treatment is tailored to the individual, targeting tension and pain at its source. With a calm, attentive approach, he helps men slow down, move more freely and reconnect with their natural balance.",
  },
];

// Gallery images (uploaded by Nur Therapy).
export const GALLERY = [
  "https://customer-assets-39nsmqrw.emergentagent.net/job_nurtherapy-white/artifacts/wklq18er_image.png",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_nurtherapy-white/artifacts/ai94auyv_image.png",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_nurtherapy-white/artifacts/gl9qqsrg_image.png",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_nurtherapy-white/artifacts/zidrsdy9_image.png",
  "https://customer-assets-39nsmqrw.emergentagent.net/job_nurtherapy-white/artifacts/uzj9l8qy_WhatsApp%20Image%202026-07-28%20at%2015.59.41.jpeg",
];
